import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getListPriceBook } from "../../../services";
import { PriceBookWithCounts } from "../../../types/price-book.type";
import { featureAccess } from "../../../permissions/access";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Skeleton from "../../ReprintAuth/Skeleton";
import CataloguePagination from "../shared/Pagination";
import CustomerListModal from "./Details/CustomerListModal";
import DeletePriceBookDialog from "./DeletePriceBookDialog";
import PriceBookTableRow from "./Row";
import {
  DRAFT_FILTER_VALUE,
  SCHEDULED_FILTER_VALUE,
  toPriceBookType,
} from "./constants";
import { PriceBookDeleteTarget, PriceBookRow } from "./types";
import {
  CATALOGUE_CHIP_HEIGHT,
  catalogueRowHeight,
  cleanParam,
  cleanStatusParam,
  priceBookDetailsPath,
} from "../shared/constants";

const compact = "@media " + COMPACT_QUERY;

const LIMIT = 10;

/** The pricebook-type chip is the tallest thing in a row, so it sets the row height. */
const ROW_HEIGHT = catalogueRowHeight(CATALOGUE_CHIP_HEIGHT);

const COLUMNS: { label: string; width?: string }[] = [
  { label: "ลำดับ", width: "4%" },
  { label: "รหัสสมุดราคา", width: "7%" },
  { label: "ชื่อสมุดราคา", width: "15%" },
  { label: "ประเภท", width: "6%" },
  { label: "ส่วนลดสมุด", width: "6%" },
  { label: "สาขาที่ใช้", width: "7%" },
  { label: "จำนวนสินค้า", width: "6%" },
  { label: "ลูกค้าที่ใช้", width: "6%" },
  { label: "สถานะ", width: "7%" },
  { label: "วันที่สร้าง", width: "9%" },
  { label: "แก้ไขเมื่อ", width: "9%" },
  { label: "แก้ไขโดย", width: "8%" },
  { label: "การดำเนินการ" },
];

/**
 * The status select carries ร่าง and มีตั้งเวลา next to the two stored statuses. ร่าง is the
 * `is_draft` flag, มีตั้งเวลา the `has_schedule` one, and ใช้งาน means a live book, so it leaves the
 * drafts out.
 */
const toStatusFilters = (
  status: string,
): { status?: "enable" | "disable"; is_draft?: boolean; has_schedule?: boolean } => {
  if (status === DRAFT_FILTER_VALUE) return { is_draft: true };
  if (status === SCHEDULED_FILTER_VALUE) return { has_schedule: true };
  const storedStatus = cleanStatusParam(status);
  if (storedStatus === "enable") return { status: storedStatus, is_draft: false };
  return { status: storedStatus };
};

const toDeleteTarget = (priceBook: PriceBookRow): PriceBookDeleteTarget => ({
  uuid: priceBook.uuid,
  code: priceBook.priceBookCode,
  name: priceBook.priceBookName,
  status: priceBook.priceBookStatus,
  branchCount: priceBook.branchCount,
  productCount: priceBook.productCount,
  isDraft: priceBook.isDraft,
});

interface PriceBookTableProps {
  search: string;
  priceBookType: string;
  status: string;
  /** A branch's company id, or the "all" sentinel. */
  branchCompanyId: string;
  createdFrom: string;
  createdTo: string;
  triggerFetch: number;
  /** Bumped when a pricebook was just created, so the list returns to its first page. */
  triggerFirstPage: number;
  onOpenDuplicateDrawer: (uuid: string) => void;
}

export default function PriceBookTable({
  search,
  priceBookType,
  status,
  branchCompanyId,
  createdFrom,
  createdTo,
  triggerFetch,
  triggerFirstPage,
  onOpenDuplicateDrawer,
}: PriceBookTableProps) {
  const navigate = useNavigate();
  const role = useUserRole();
  const canEditCatalogue = featureAccess.productWrite(role);

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [priceBooks, setPriceBooks] = useState<PriceBookRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] =
    useState<PriceBookDeleteTarget | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string>("");
  // The pricebook whose customers the modal lists; empty while it is closed.
  const [customersPriceBookUuid, setCustomersPriceBookUuid] =
    useState<string>("");

  // Marks which list request the screen is waiting for, so an answer that a filter
  // change, a page change or the refresh after a delete has already superseded is
  // dropped instead of overwriting the rows the user is looking at.
  const latestRequestRef = useRef<number>(0);

  // The filters arrive as props, so the list has to fall back to page 1 in the same
  // render they change in — restarting the page from an effect would fire one request
  // at the old page first. A brand-new pricebook needs the same jump: the list is read
  // newest-first, so it is the very first row and invisible from any later page.
  const firstPageKey = [
    search,
    priceBookType,
    status,
    branchCompanyId,
    createdFrom,
    createdTo,
    triggerFirstPage,
  ].join("|");
  const [appliedFirstPageKey, setAppliedFirstPageKey] =
    useState<string>(firstPageKey);
  if (appliedFirstPageKey !== firstPageKey) {
    setAppliedFirstPageKey(firstPageKey);
    setPage(1);
  }

  const getPriceBooks = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getListPriceBook(page, limit, {
        search: search || undefined,
        // The select's "ทั้งหมด" sentinel is not a type, so narrowing it drops it.
        type: toPriceBookType(priceBookType),
        ...toStatusFilters(status),
        branch_id: cleanParam(branchCompanyId),
        created_from: createdFrom || undefined,
        created_to: createdTo || undefined,
      });
      if (latestRequestRef.current !== requestId) return;

      const formatted: PriceBookRow[] = data.rows.map(
        (priceBook: PriceBookWithCounts, index: number) => ({
          uuid: priceBook.uuid,
          no: (page - 1) * limit + index + 1,
          priceBookCode: priceBook.code,
          priceBookName: priceBook.name,
          priceBookType: priceBook.type,
          priceBookStatus: priceBook.status,
          productCount: priceBook.product_count,
          activeStoreCount: priceBook.active_store_count,
          branchCount: priceBook.branch_count,
          branchScope: priceBook.branch_scope,
          discountPercent: priceBook.discount_percent,
          isDraft: priceBook.is_draft,
          expiringPeriodCount: priceBook.expiring_period_count,
          nextScheduleDate: priceBook.next_schedule_date,
          validity: {
            effective_from: priceBook.effective_from,
            effective_to: priceBook.effective_to,
          },
          createdAt: priceBook.created_at,
          updatedAt: priceBook.updated_at,
          updatedByName: priceBook.updated_by_name,
        }),
      );

      setPriceBooks(formatted);
      setTotalCount(data.count);
    } finally {
      if (latestRequestRef.current === requestId) setLoading(false);
    }
  }, [
    page,
    limit,
    search,
    priceBookType,
    status,
    branchCompanyId,
    createdFrom,
    createdTo,
  ]);

  useEffect(() => {
    getPriceBooks();
  }, [triggerFetch, getPriceBooks]);

  const handleView = (uuid: string) => {
    navigate(priceBookDetailsPath(uuid));
  };

  const handleOpenCustomers = (uuid: string) => {
    setCustomersPriceBookUuid(uuid);
  };

  const handleCloseCustomers = () => {
    setCustomersPriceBookUuid("");
  };

  const handleAskDelete = (priceBook: PriceBookRow) => {
    setConflictMessage("");
    setDeleteTarget(toDeleteTarget(priceBook));
  };

  const handleCloseDeleteDialog = () => {
    setDeleteTarget(null);
  };

  // A refusal is a business rule the global toast only flashes, so the alert keeps it on
  // screen while the user goes to fix it.
  const handleDeleteRefused = (message: string) => {
    setConflictMessage(message);
  };

  const handleDismissConflict = () => setConflictMessage("");

  return (
    <Paper>
      {conflictMessage && (
        <Alert severity="warning" onClose={handleDismissConflict} sx={{ mb: 2 }}>
          {conflictMessage}
        </Alert>
      )}
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell
                  key={column.label}
                  align="center"
                  sx={{
                    borderRight: (theme) =>
                      `1px solid ${theme.palette.draft.main}`,
                    "&:last-of-type": { borderRight: "none" },
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton
                columns={COLUMNS.length}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}
            {!loading && priceBooks.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 5, [compact]: { py: 3 } }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลสมุดราคา
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              priceBooks.map((priceBook, index) => (
                <PriceBookTableRow
                  key={priceBook.uuid}
                  priceBook={priceBook}
                  rowHeight={ROW_HEIGHT}
                  striped={index % 2 === 1}
                  canEdit={canEditCatalogue}
                  onView={handleView}
                  onOpenCustomers={handleOpenCustomers}
                  onDuplicate={onOpenDuplicateDrawer}
                  onDelete={handleAskDelete}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />

      <Typography
        variant="caption"
        color="text.secondary"
        component="p"
        sx={{ px: 1, pb: 1 }}
      >
        การผูกสมุดราคาให้ลูกค้า ทำที่หน้ารายละเอียดลูกค้า
      </Typography>

      <CustomerListModal
        open={Boolean(customersPriceBookUuid)}
        priceBookUuid={customersPriceBookUuid}
        onClose={handleCloseCustomers}
      />

      <DeletePriceBookDialog
        target={deleteTarget}
        onClose={handleCloseDeleteDialog}
        onDeleted={getPriceBooks}
        onDeactivated={getPriceBooks}
        onRefused={handleDeleteRefused}
      />
    </Paper>
  );
}
